import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CAMPAIGN_COLUMNS, CAMPAIGN_ACTIONS } from '../data/constants/campaigns.constants';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ApiCampaignsService } from '../data/services/api/api-campaigns.service';
import { CampaignService } from '../data/services/view/campaign.service';
import { CampaignDashboardComponent } from '../components/campaign-dashboard/campaign-dashboard.component';
import { MainTableComponent } from '@shared/components/tables/main-table/main-table.component';

@Component({
  selector: 'app-campaigns-list',
  templateUrl: './campaigns-list.component.html',
  styleUrls: ['./campaigns-list.component.scss'],
  providers: [MessageService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CampaignsListComponent implements OnInit, OnDestroy {
  @ViewChild(CampaignDashboardComponent) dashboardComponent?: CampaignDashboardComponent;
  @ViewChild(MainTableComponent) mainTable?: MainTableComponent;
  
  columns = CAMPAIGN_COLUMNS;
  actions = CAMPAIGN_ACTIONS;
  showDashboard = true;
  campaigns: any[] = []; // Almacenar campañas para el dashboard
  
  // Error handling
  hasError = false;
  errorMessage = '';
  
  // Loading & Empty states
  isLoading = false;
  emptyMessage = 'No hay campañas activas. Las campañas se generan automáticamente al ejecutar estrategias';
  
  additionalMainActions = [
    { 
      label: 'Dashboard', 
      icon: 'fa-solid fa-chart-line', 
      action: 'TOGGLE_DASHBOARD'
    },
    { 
      label: 'Auto-Refresh', 
      icon: 'fa-solid fa-sync', 
      action: 'TOGGLE_REFRESH'
    }
  ];

  constructor(
    public apiCampaignsService: ApiCampaignsService,
    public campaignService: CampaignService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Cargar campañas iniciales para el dashboard
    this.loadCampaigns();
    
    // Iniciar auto-refresh
    this.campaignService.startAutoRefresh(() => {
      this.refreshData();
    });
  }

  ngOnDestroy() {
    // Detener auto-refresh para evitar memory leaks
    this.campaignService.stopAutoRefresh();
  }

  /**
   * Transforma una campaña agregando campos computed
   */
  private transformCampaign(campaign: any): any {
    return {
      ...campaign,
      // Campos de populate (extraer de relaciones si vienen populated)
      strategyName: campaign.strategyId?.name || campaign.strategyName || 'N/A',
      leaseNumber: campaign.leaseId?.leaseNumber || campaign.leaseNumber || campaign.leaseId || 'N/A',
      clientName: campaign.clientId?.name || campaign.clientName || 'N/A',
      // Campos computed
      channelsDisplay: campaign.channels?.join(', ') || 'N/A',
      resultsDisplay: this.campaignService.getChannelResultsDisplay(campaign)
    };
  }

  refreshData() {
    // Refrescar dashboard y registros de la tabla
    this.loadCampaigns();
    // Refrescar los registros de la tabla (MainTableComponent)
    this.mainTable?.findRecords();
  }

  /**
   * Carga las campañas para el dashboard
   */
  loadCampaigns() {
    this.apiCampaignsService.getAll(1, 100).subscribe({
      next: (response) => {
        if (!response.error && response.data?.data) {
          this.campaigns = response.data.data;
          this.cdr.detectChanges(); // Forzar detección de cambios
        }
      },
      error: (error) => {
        console.error('Error al cargar campañas:', error);
      }
    });
  }

  handleTableAction(event: any) {
    const { action, data } = event;
    
    switch (action) {
      case 'EXECUTE_NOW':
        this.executeNow(data._id);
        break;
      case 'CANCEL':
        this.cancelCampaign(data._id);
        break;
    }
  }

  handleMainAction(action: string) {
    switch (action) {
      case 'TOGGLE_DASHBOARD':
        this.showDashboard = !this.showDashboard;
        this.cdr.markForCheck();
        break;
      case 'TOGGLE_REFRESH':
        this.campaignService.toggleAutoRefresh(() => {
          this.refreshData();
        });
        
        // Notificación con mejor UX: verde para activado, naranja para desactivado
        if (this.campaignService.autoRefreshEnabled) {
          this.messageService.add({
            severity: 'success',
            summary: '✓ Auto-Refresh Activado',
            detail: 'Los datos se actualizarán automáticamente cada 30 segundos',
            life: 4000
          });
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: '⊗ Auto-Refresh Desactivado',
            detail: 'Los datos no se actualizarán automáticamente. Actualiza manualmente cuando lo necesites',
            life: 4000
          });
        }
        
        // Forzar detección de cambios en este componente
        this.cdr.markForCheck();
        
        // Forzar detección de cambios en el componente dashboard (si está visible)
        if (this.dashboardComponent) {
          this.dashboardComponent.detectChanges();
        }
        break;
    }
  }

  executeNow(id: string) {
    this.confirmationService.confirm({
      message: '¿Ejecutar esta campaña inmediatamente?',
      header: 'Confirmar Ejecución',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Ejecutar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.apiCampaignsService.executeNow(id).subscribe((r) => {
          if (r.error) {
            return this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: r.msg || 'No se pudo ejecutar la campaña'
            });
          }
          
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Campaña ejecutada correctamente'
          });
          
          this.refreshData();
        });
      }
    });
  }

  cancelCampaign(id: string) {
    this.confirmationService.confirm({
      message: '¿Cancelar esta campaña? Esta acción no se puede deshacer.',
      header: 'Confirmar Cancelación',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      acceptLabel: 'Sí, Cancelar',
      rejectLabel: 'No',
      accept: () => {
        this.apiCampaignsService.cancel(id).subscribe((r) => {
          if (r.error) {
            return this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: r.msg || 'No se pudo cancelar la campaña'
            });
          }
          
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Campaña cancelada correctamente'
          });
          
          this.refreshData();
        });
      }
    });
  }

  /**
   * Maneja errores de forma centralizada
   */
  handleError(error: any): void {
    this.hasError = true;
    this.errorMessage = error.message || 'Ocurrió un error al cargar los datos';
    console.error('Error in CampaignsListComponent:', error);
    
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: this.errorMessage,
      life: 5000
    });
    this.cdr.markForCheck();
  }

  /**
   * Reintenta la operación después de un error
   */
  retry(): void {
    this.hasError = false;
    this.errorMessage = '';
    window.location.reload();
  }
}
